import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import WorkflowDocumentView from "@/components/documents/WorkflowDocumentView";
import DocumentFields from "@/components/documents/DocumentFields";
import { TotoAdaptiveDock } from "@/components/toto/TotoAdaptiveSurface";
import "./documents-space.css";
import "@/components/documents/document-studio.css";
import Topbar from "@/components/Topbar";
import AppLoadingShell from "@/components/AppLoadingShell";
import { toast } from "sonner";
import {
  ArrowLeft, Pencil, Archive as ArchiveIcon, CheckCircle2,
  MessageCircle, Send, Eye,
} from "lucide-react";

const KIND_LABELS = { guide:"Guide", process:"Process", policy:"Policy", reference:"Reference", form:"Form" };

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}
function fmtRel(iso) {
  if (!iso) return "";
  const daysAgo = Math.round((Date.now() - new Date(iso).getTime()) / 86400000);
  if (daysAgo === 0) return "today";
  if (daysAgo === 1) return "yesterday";
  if (daysAgo < 30) return `${daysAgo}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function DocumentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/documents/${id}`);
      setDoc(r.data);
      setErr(null);
    } catch (e) {
      setErr(e.response?.data?.detail || "Document not available.");
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    try { await api.post(`/documents/${id}/publish`); toast.success("Published."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Publish failed."); }
  };
  const archive = async () => {
    if (!window.confirm("Archive this document?")) return;
    try { await api.post(`/documents/${id}/archive`); toast.success("Archived."); nav("/documents"); }
    catch (e) { toast.error(e.response?.data?.detail || "Archive failed."); }
  };
  const markReviewed = async () => {
    try { await api.post(`/documents/${id}/mark-reviewed`); toast.success("Marked as reviewed."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  const submitSuggestion = async () => {
    if (!suggestion.trim()) { toast.error("What would you improve?"); return; }
    try {
      await api.post(`/documents/${id}/suggestions`, { note: suggestion });
      toast.success("Suggestion sent to leadership. Thank you.");
      setSuggesting(false);
      setSuggestion("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send suggestion.");
    }
  };

  if (err) {
    return (
      <div>
        <Topbar title="Documents" />
        <div className="px-4 sm:px-10 pb-12 max-w-2xl mx-auto text-center" data-testid="doc-error">
          <p className="text-sm text-stone-500">{err}</p>
          <Link to="/documents" className="text-xs text-stone-700 underline mt-2 inline-block">← Documents</Link>
        </div>
      </div>
    );
  }
  if (!doc) return <AppLoadingShell />;

  if (doc.workflow) return <WorkflowDocumentView initialDocument={doc} onSaved={load}/>;

  if (editing) {
    return <DocumentEditor doc={doc} onCancel={() => setEditing(false)} onSaved={(d) => { setDoc(d); setEditing(false); }} />;
  }

  const draft = doc.status === "draft";
  const archived = doc.status === "archived";
  const stale = doc.last_reviewed_at ? (Date.now() - new Date(doc.last_reviewed_at).getTime()) / 86400000 > 365 : false;

  return (
    <DocumentFrame title="Documents">
      <article className="px-4 sm:px-10 pb-16 max-w-3xl mx-auto stagger-fade" data-testid="document-detail">
        <div className="mb-6 flex items-center justify-between flex-wrap gap-2">
          <Link to="/documents" data-testid="doc-back-to-library" className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-900">
            <ArrowLeft size={12} /> Documents
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            {doc.can_edit && draft && (
              <button onClick={publish} data-testid="doc-publish" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs bg-emerald-600 text-white hover:bg-emerald-700">
                <CheckCircle2 size={12} /> Publish
              </button>
            )}
            {doc.can_edit && !archived && (
              <>
                <button onClick={() => setEditing(true)} data-testid="doc-edit" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs bg-stone-900 text-white hover:bg-stone-800">
                  <Pencil size={11} /> Edit document
                </button>
                <button onClick={markReviewed} data-testid="doc-mark-reviewed" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-white subtle-border text-stone-700 hover:bg-stone-50">
                  <CheckCircle2 size={11} /> Mark reviewed
                </button>
                <button onClick={archive} data-testid="doc-archive" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-white subtle-border text-stone-500 hover:text-rose-700 hover:border-rose-200">
                  <ArchiveIcon size={11} /> Archive
                </button>
              </>
            )}
          </div>
        </div>

        {/* Header */}
        <header className="mb-8">
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-stone-500 mb-2">
            <span>{KIND_LABELS[doc.kind] || doc.kind}</span>
            {doc.category && <><span className="text-stone-300">·</span><span>{doc.category}</span></>}
            {(doc.boutique_scope || []).length > 0 && (
              <><span className="text-stone-300">·</span><span>{doc.boutique_scope.join(", ")}</span></>
            )}
            <span className="text-stone-300">·</span><span>v{doc.version}</span>
            {draft &&    <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-100 text-amber-900">Draft</span>}
            {archived && <span className="ml-2 px-2 py-0.5 rounded-full bg-stone-100 text-stone-500">Archived</span>}
            {stale && !draft && !archived && <span className="ml-2 px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">Review overdue</span>}
          </div>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-stone-900 leading-tight" data-testid="doc-title">{doc.title}</h1>
          <div className="mt-3 text-[12px] text-stone-500 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>Owner · {doc.owner_name || "—"}</span>
            <span>·</span>
            <span>Updated · {fmtDate(doc.updated_at)}</span>
            {doc.last_viewed_by && (
              <>
                <span>·</span>
                <span className="inline-flex items-center gap-1 text-stone-400" data-testid="doc-last-viewed">
                  <Eye size={10} /> Last viewed by {doc.last_viewed_by.name?.split(" ")[0]} {fmtRel(doc.last_viewed_by.viewed_at)}
                </span>
              </>
            )}
          </div>
        </header>

        {/* Why this matters — the human paragraph */}
        {doc.why_this_matters && (
          <section className="mb-8 document-reader-why" data-testid="doc-why">
            <h2 className="document-reader-heading">Why this matters</h2>
            <p className="text-[15px] text-stone-800 leading-relaxed whitespace-pre-wrap">{doc.why_this_matters}</p>
          </section>
        )}

        {doc.body && <section className="mb-8 whitespace-pre-wrap text-sm leading-relaxed" data-testid="document-body">{doc.body}</section>}

        {/* Purpose + Scope */}
        {(doc.purpose || doc.scope) && (
          <section className="mb-8 grid gap-6">
            {doc.purpose && (
              <div>
                <div className="label-eyebrow mb-1.5">Purpose</div>
                <p className="text-[14px] text-stone-700 leading-relaxed">{doc.purpose}</p>
              </div>
            )}
            {doc.scope && (
              <div>
                <div className="label-eyebrow mb-1.5">Scope</div>
                <p className="text-[14px] text-stone-700 leading-relaxed">{doc.scope}</p>
              </div>
            )}
          </section>
        )}

        {/* Definitions */}
        {(doc.definitions || []).length > 0 && (
          <section className="mb-8">
            <div className="label-eyebrow mb-3">Definitions</div>
            <dl className="space-y-2">
              {doc.definitions.map((d, idx) => (
                <div key={idx} className="rounded-xl bg-stone-50 border border-stone-100 px-4 py-3">
                  <dt className="text-[13px] text-stone-900 font-medium">{d.term}</dt>
                  <dd className="text-[13px] text-stone-600 mt-0.5 leading-relaxed">{d.definition}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {/* Steps */}
        {(doc.steps || []).length > 0 && (
          <section className="mb-8">
            <div className="label-eyebrow mb-3">Steps</div>
            <ol className="space-y-3">
              {doc.steps.map((s, idx) => (
                <li key={idx} className="document-reader-step" data-testid={`doc-step-${s.n || idx+1}`}>
                  <div className="flex items-baseline gap-3">
                    <span className="font-serif-display text-2xl text-stone-300 leading-none shrink-0">{s.n || idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[15px] text-stone-900">{s.title}</div>
                      <p className="text-[13.5px] text-stone-600 mt-1.5 leading-relaxed">{s.detail}</p>
                      {s.if_this_happens && (
                        <div className="mt-3 text-[12.5px] text-amber-800 leading-relaxed pl-3 border-l-2 border-amber-200">
                          <span className="italic">If this happens…</span> {s.if_this_happens}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Related links */}
        {(doc.related_links || []).length > 0 && (
          <section className="mb-8">
            <div className="label-eyebrow mb-3">Related</div>
            <ul className="space-y-1">
              {doc.related_links.map((r, idx) => (
                <li key={idx}>
                  {r.url
                    ? <a href={r.url} target="_blank" rel="noreferrer" className="text-[13px] text-stone-700 underline decoration-stone-300 hover:decoration-stone-900">{r.label}</a>
                    : <span className="text-[13px] text-stone-500">{r.label}</span>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Common questions */}
        {(doc.common_questions || []).length > 0 && (
          <section className="mb-8" data-testid="doc-common-questions">
            <div className="label-eyebrow mb-3">Common questions</div>
            <div className="space-y-2">
              {doc.common_questions.map((q, idx) => (
                <details key={idx} className="rounded-2xl bg-white subtle-border card-shadow group">
                  <summary className="px-5 py-3 cursor-pointer text-[14px] text-stone-900 flex items-center justify-between">
                    <span>{q.q}</span>
                    <span className="text-stone-400 group-open:rotate-90 transition-transform">›</span>
                  </summary>
                  <div className="px-5 pb-4 text-[13.5px] text-stone-700 leading-relaxed">{q.a}</div>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Suggest an improvement — anyone can */}
        <section className="mb-6">
          {!suggesting ? (
            <button
              onClick={() => setSuggesting(true)}
              data-testid="doc-suggest"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs bg-white subtle-border text-stone-700 hover:bg-stone-50"
            >
              <MessageCircle size={11} /> Suggest an improvement
            </button>
          ) : (
            <div className="rounded-2xl border border-stone-200 bg-white p-4" data-testid="doc-suggest-panel">
              <div className="label-eyebrow mb-2">Your suggestion</div>
              <textarea
                value={suggestion}
                onChange={(e) => setSuggestion(e.target.value)}
                data-testid="doc-suggest-note"
                rows={3}
                placeholder="What could be clearer, kinder, or more accurate?"
                className="w-full px-3 py-2 rounded-xl subtle-border text-sm bg-white outline-none focus:border-stone-400 resize-none"
              />
              <div className="mt-2 flex justify-end gap-2">
                <button onClick={() => { setSuggesting(false); setSuggestion(""); }} className="px-3 py-1.5 text-xs text-stone-500 hover:bg-stone-100 rounded-full">Cancel</button>
                <button onClick={submitSuggestion} data-testid="doc-suggest-submit" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs bg-stone-900 text-white hover:bg-stone-800">
                  <Send size={11} /> Send to leadership
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Version history (leadership only, collapsed) */}
        {doc.can_edit && (doc.version_history || []).length > 0 && (
          <details className="text-[12px] text-stone-500" data-testid="doc-version-history">
            <summary className="cursor-pointer inline-flex items-center gap-1.5 hover:text-stone-900">Version history</summary>
            <ul className="mt-2 pl-4 space-y-1">
              {[...doc.version_history].reverse().map((h, idx) => (
                <li key={idx} className="text-stone-500">
                  v{h.version} · {fmtDate(h.changed_at)} · {h.changed_by_name} · <span className="italic">{h.note}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </article>
    </DocumentFrame>
  );
}


function DocumentFrame({ title, children }) {
  return <main className="documents-space document-detail-space">
    <section className="documents-space__vessel" aria-label={title}>
      <header className="documents-space__header"><div><h1>Documents</h1></div></header>
      <div className="documents-space__content document-detail-scroll">{children}</div>
    </section>
    <TotoAdaptiveDock active="workspace" />
  </main>;
}

/* ─── Editor (leadership) ─────────────────────────────────────────────── */
function DocumentEditor({ doc, onCancel, onSaved }) {
  const [form, setForm] = useState({
    title: doc.title || "",
    body: doc.body || "",
    category: doc.category || "",
    boutique_scope: doc.boutique_scope || [],
    why_this_matters: doc.why_this_matters || "",
    purpose: doc.purpose || "",
    scope: doc.scope || "",
    definitions: doc.definitions || [],
    steps: doc.steps || [],
    related_links: doc.related_links || [],
    common_questions: doc.common_questions || [],
    change_note: "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.title.trim()) { toast.error("Add a title before saving."); return; }
    setSaving(true);
    try {
      const res = await api.patch(`/documents/${doc.id}`, form);
      toast.success("Saved.");
      onSaved(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <DocumentFrame title="Edit document">
      <div className="px-4 sm:px-10 pb-16 max-w-3xl mx-auto space-y-5" data-testid="document-editor">
        <div className="flex items-center justify-between gap-2 mb-2">
          <button onClick={onCancel} disabled={saving} className="text-xs text-stone-500 hover:text-stone-900">← Cancel</button>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={save} disabled={saving} data-testid="editor-save" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        <DocumentFields value={form} onChange={set} disabled={saving} />

        <Field label="Change note (helps history)">
          <input value={form.change_note} onChange={(e) => set("change_note", e.target.value)} data-testid="editor-change-note" placeholder="e.g. Rewrote 'Why this matters' after the Feb meeting" className="w-full px-4 py-2 rounded-full subtle-border text-sm bg-white" />
        </Field>
      </div>
    </DocumentFrame>
  );
}

function Field({ label, tint, children }) {
  return (
    <div className={tint ? "rounded-2xl bg-emerald-50/40 border border-emerald-100 p-4" : ""}>
      <label className={`label-eyebrow block mb-1.5 ${tint ? "text-emerald-900" : ""}`}>{label}</label>
      {children}
    </div>
  );
}
